import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { memo, useState, useEffect } from "react";

const Header = memo(() => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Bloquear scroll do body quando menu mobile está aberto
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const mainMenuItems = [
    { name: "Início", id: "inicio" },
    { name: "Parceiros", id: "parceiros" },
    { name: "Quem Somos", id: "quem-somos" },
    { name: "Contato", id: "fale-conosco" },
  ];

  const dropdownItems = [
    { name: "Para Quem", id: "target-cards" },
    { name: "Serviços", id: "servicos" },
    { name: "Áreas de Atuação", id: "areas-atuacao" },
    { name: "Como Trabalhamos", id: "processo" },
    { name: "Resultados", id: "resultados" },

    { name: "LGPD", id: "lgpd" },
    { name: "Alertas", id: "alertas" },
    { name: "FAQ", id: "faq" },
    { name: "Trabalhe Conosco", id: "trabalhe-conosco" },
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setDropdownOpen(false);
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownOpen &&
        !(event.target as Element).closest(".dropdown-container")
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-gray-200/50 shadow-sm"
      style={{
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)",
      }}
    >
      <div className="container mx-auto px-4 md:px-6 py-3 max-w-[1400px]">
        <div className="flex items-center justify-between">
          <motion.div
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => scrollToSection("inicio")}
          >
            <img
              src="https://res.cloudinary.com/dy5qtniog/image/upload/f_auto,q_auto/v1774641428/logo-va_scgbix.png"
              alt="V.A. Logo"
              className="h-9 w-auto"
              width="auto"
              height={36}
            />
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold bg-gradient-to-r from-gray-900 via-red-800 to-gray-800 bg-clip-text text-transparent">
                V.A.
              </h1>
              <p className="text-xs font-semibold bg-gradient-to-r from-red-600 via-gray-600 to-red-600 bg-clip-text text-transparent tracking-wider">
                RECUPERAÇÃO DE CRÉDITO
              </p>
            </div>
          </motion.div>

          <motion.nav
            className="hidden lg:flex items-center space-x-4"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.08, delayChildren: 0.3 },
              },
            }}
          >
            {mainMenuItems.map((item) => (
              <motion.button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                variants={{
                  hidden: { opacity: 0, y: -20 },
                  visible: { opacity: 1, y: 0 },
                }}
                className="text-gray-700 hover:text-[#F22E3E] font-medium transition-all duration-300 relative group px-3 py-2 rounded-lg hover:bg-gradient-to-r hover:from-red-50/50 hover:to-pink-50/50 text-sm"
              >
                {item.name}
                <span className="absolute -bottom-1 left-3 w-0 h-0.5 bg-gradient-to-r from-[#F22E3E] to-[#BF2431] transition-all duration-300 group-hover:w-[calc(100%-1.5rem)] rounded-full"></span>
              </motion.button>
            ))}

            {/* Dropdown "Mais" */}
            <div className="relative dropdown-container">
              <motion.button
                variants={{
                  hidden: { opacity: 0, y: -20 },
                  visible: { opacity: 1, y: 0 },
                }}
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
                className="text-gray-700 hover:text-[#F22E3E] font-medium transition-all duration-300 relative group px-3 py-2 rounded-lg hover:bg-gradient-to-r hover:from-red-50/50 hover:to-pink-50/50 text-sm flex items-center space-x-1"
              >
                <span>Mais</span>
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                <span className="absolute -bottom-1 left-3 w-0 h-0.5 bg-gradient-to-r from-[#F22E3E] to-[#BF2431] transition-all duration-300 group-hover:w-[calc(100%-1.5rem)] rounded-full"></span>
              </motion.button>

              {/* Dropdown Menu */}
              {dropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full right-0 mt-2 w-56 bg-white backdrop-blur-lg border border-gray-200 rounded-xl shadow-xl py-2 z-50"
                >
                  {dropdownItems.map((item) => (
                    <motion.button
                      key={item.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      onClick={() => scrollToSection(item.id)}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:text-[#F22E3E] hover:bg-gradient-to-r hover:from-red-50/50 hover:to-pink-50/50 transition-all duration-200 border-b border-gray-100/50 last:border-b-0"
                    >
                      {item.name}
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </div>
          </motion.nav>

          <div className="flex items-center space-x-4">
            {/* Botão CTA principal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                className="text-white px-6 py-2 rounded-xl font-medium transition-all duration-300 hover:shadow-lg relative overflow-hidden group"
                style={{
                  background:
                    "linear-gradient(135deg, #F22E3E 0%, #BF2431 50%, #F20505 100%)",
                }}
                aria-label="Negociar dívidas pelo WhatsApp (abre em nova aba)"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <a
                  href="https://api.whatsapp.com/send?phone=15558510852&text=Ol%C3%A1%2C%20gostaria%20de%20tirar%20uma%20d%C3%BAvida."
                  target="_blank"
                >
                  <span className="relative">Renegocie Online</span>
                </a>
              </Button>
            </motion.div>

            {/* Menu mobile hamburger */}
            <div className="lg:hidden">
              <button
                className="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-label="Abrir menu mobile"
              >
                <svg
                  className="w-6 h-6 text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>

            {/* Mobile menu overlay */}
            {mobileMenuOpen && (
              <div
                className="fixed inset-0 w-screen h-screen lg:hidden"
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: "100vw",
                  height: "100vh",
                  backgroundColor: "#ffffff",
                  background: "#ffffff",
                  backgroundImage: "none",
                  backgroundAttachment: "scroll",
                  backgroundClip: "border-box",
                  backgroundOrigin: "padding-box",
                  backgroundPosition: "0% 0%",
                  backgroundRepeat: "repeat",
                  backgroundSize: "auto",
                  backdropFilter: "none",
                  WebkitBackdropFilter: "none",
                  filter: "none",
                  opacity: 1,
                  visibility: "visible",
                  zIndex: 9999,
                  boxShadow: "none",
                }}
              >
                <motion.div
                  initial={{ x: 300 }}
                  animate={{ x: 0 }}
                  exit={{ x: 300 }}
                  transition={{ type: "spring", stiffness: 260, damping: 25 }}
                  className="w-full h-full p-6 flex flex-col gap-6"
                  style={{
                    backgroundColor: "#ffffff",
                    background: "#ffffff",
                    width: "100%",
                    height: "100%",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-label="Fechar menu"
                    style={{ zIndex: 10000 }}
                  >
                    <svg
                      className="w-6 h-6 text-gray-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                  <nav className="flex flex-col gap-2 mt-8">
                    {mainMenuItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          scrollToSection(item.id);
                          setMobileMenuOpen(false);
                        }}
                        className="text-gray-800 text-lg font-medium py-2 px-2 rounded hover:bg-red-50 text-left transition-all"
                        aria-label={`Navegar para a seção ${item.name}`}
                      >
                        {item.name}
                      </button>
                    ))}
                    <div className="border-t border-gray-200 my-2"></div>
                    {dropdownItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          scrollToSection(item.id);
                          setMobileMenuOpen(false);
                        }}
                        className="text-gray-600 text-base py-2 px-2 rounded hover:bg-gray-50 text-left transition-all"
                        aria-label={`Navegar para a seção ${item.name}`}
                      >
                        {item.name}
                      </button>
                    ))}
                  </nav>
                  <Button
                    className="mt-8 w-full bg-gradient-to-r from-red-600 to-red-500 text-white rounded-xl py-3 font-semibold shadow-md hover:shadow-lg"
                    aria-label="Negociar dívidas pelo WhatsApp (abre em nova aba)"
                  >
                    <a
                      href="https://api.whatsapp.com/send?phone=15558510852&text=Ol%C3%A1%2C%20gostaria%20de%20tirar%20uma%20d%C3%BAvida."
                      target="_blank"
                    >
                      Renegocie Online
                    </a>
                  </Button>
                </motion.div>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.header>
  );
});

Header.displayName = "Header";

export default Header;
